import { AxiosError } from 'axios';

export enum LogType {
  ApplicationHighlyConfidential = 'ApplicationHighlyConfidential',
}

function populateError(params?: Record<string, unknown>, error?: Error): Record<string, unknown> {
  if (!error) {
    return {
      ...(params ?? {}),
    };
  }

  return {
    ...(params ?? {}),
    errorName: error.name,
    errorMessage: error.message,
    stackTrace: error.stack,
  };
}

function log(
  level: 'info' | 'warn' | 'error',
  message: string,
  params?: Record<string, unknown>,
): void {
  const timestamp = new Date().toISOString();
  const record = {
    timestamp,
    level,
    message,
    logType: LogType.ApplicationHighlyConfidential,
    params,
  };
  console[level](JSON.stringify(record));
}

export const logError = (
  message: string,
  params?: Record<string, unknown>,
  error?: Error,
): void => {
  log('error', message, populateError(params, error));
};

export const logInfo = (message: string, params?: Record<string, unknown>): void => {
  log('info', message, params);
};

export const logWarn = (message: string, params?: Record<string, unknown>, error?: Error): void => {
  log('warn', message, populateError(params, error));
};

export enum ApiTypes {
  listing_api = 'listing details',
  search_api = 'search results',
  geo_api = 'geo results',
  geo_extra_api = 'geo extra results',
}

export const WARNING_LEVEL_HTTP_CODES = {
  /* 404 - Not Found, as we can not prevent requests for locations which do no exist in our system
      the case is logged as warn */
  [ApiTypes.geo_api]: [404],

  /* 404 - Not Found, e.g. listing does not exist */
  [ApiTypes.listing_api]: [404],

  /* 416 - Requested Range Not Satisfiable, e.g. page number is too high */
  [ApiTypes.search_api]: [416],
};

/**
 * All logs are ingested to Datadog.
 * The one logged by logErrorToDatadogCustomMetric is used for generating custom metrics
 *
 * @param message - The error message.
 * @param params - Additional parameters to include in the log.
 * @param error - The error object.
 */
export const logErrorToDatadogCustomMetric = (
  subject: ApiTypes,
  params?: Record<string, unknown>,
  error?: AxiosError,
): void => {
  const customMetricMessagePattern = `Failed to fetch ${subject}`;

  const statusCode = error?.response?.status;

  if (statusCode && WARNING_LEVEL_HTTP_CODES[subject]?.includes(statusCode)) {
    logWarn(customMetricMessagePattern, params, error);
  } else {
    logError(customMetricMessagePattern, params, error);
  }
};
